import React, { useEffect, useRef, useId } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from 'components/Icons'
import styles from './Drawer.module.scss'

/**
 * Боковая панель (drawer) — для деталей записи, фильтров, форм.
 * <UnvDrawer open={x} onClose={...} side="right" size="md" title="Профиль">
 *   ... содержимое
 *   <UnvDrawer.Footer> ... </UnvDrawer.Footer>
 * </UnvDrawer>
 */

// Общий счётчик scroll-lock'а — те же body-style правила, что и в Modal.
let openCount = 0
let prevBodyOverflow = ''
const lockBodyScroll = () => {
  if (typeof document === 'undefined') return
  if (openCount === 0) {
    prevBodyOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
  }
  openCount += 1
}
const unlockBodyScroll = () => {
  if (typeof document === 'undefined') return
  openCount = Math.max(0, openCount - 1)
  if (openCount === 0) {
    document.body.style.overflow = prevBodyOverflow
  }
}

const UnvDrawer = ({
  open,
  onClose,
  title,
  side = 'right',
  size = 'md',
  closeOnOverlay = true,
  showClose = true,
  className = '',
  children,
}) => {
  const { t } = useTranslation('common')
  const overlayRef = useRef(null)
  const drawerRef = useRef(null)
  const onCloseRef = useRef(onClose)
  const titleId = useId()

  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  useEffect(() => {
    if (!open) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape' && onCloseRef.current) onCloseRef.current()
    }
    document.addEventListener('keydown', onKey)
    lockBodyScroll()
    const focusTimer = setTimeout(() => {
      const focusable = drawerRef.current?.querySelector(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )
      focusable?.focus()
    }, 50)
    return () => {
      document.removeEventListener('keydown', onKey)
      unlockBodyScroll()
      clearTimeout(focusTimer)
    }
  }, [open])

  if (!open) return null

  const handleOverlay = (e) => {
    if (closeOnOverlay && e.target === overlayRef.current && onClose) onClose()
  }

  const content = (
    <div
      ref={overlayRef}
      className={styles.overlay}
      onClick={handleOverlay}
      role="presentation"
    >
      <aside
        ref={drawerRef}
        className={`${styles.drawer} ${styles[`side_${side}`]} ${styles[`s_${size}`]} ${className}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
      >
        {(title || showClose) && (
          <div className={styles.header}>
            {title && <h3 id={titleId} className={styles.title}>{title}</h3>}
            {showClose && (
              <button
                type="button"
                onClick={onClose}
                className={styles.closeBtn}
                aria-label={t('auto.unvui_drawer.close', 'Закрыть')}
              >
                <Icon name="x" size={18} />
              </button>
            )}
          </div>
        )}
        <div className={styles.body}>{children}</div>
      </aside>
    </div>
  )

  return typeof document !== 'undefined' ? createPortal(content, document.body) : content
}

const Footer = ({ children, align = 'right' }) => (
  <div className={`${styles.footer} ${styles[`align_${align}`] || ''}`}>{children}</div>
)

UnvDrawer.Footer = Footer

export default UnvDrawer
